
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AppUser, AppSettings } from '../types';
import { 
  Plus, Shield, Palette, Key, Edit2, Database, ListTree, Activity, Briefcase, Search,
  ChevronRight, Download, EyeOff, Eye, Fingerprint, Filter, SortAsc, Upload, ChevronDown, ShieldAlert
} from 'lucide-react';
import { PasswordModal } from '../features/admin/PasswordModal';
import { UserFormModal } from '../features/admin/UserFormModal';
import { ReferenceManager } from '../features/admin/ReferenceManager';
import { BrandingForm } from '../features/admin/BrandingForm';
import { DataAuditTool } from '../features/admin/DataAuditTool';
import { Button } from '../components/ui/Button';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse';

const { useNavigate } = ReactRouterDom as any;

type AdminTab = 'users' | 'lookups' | 'branding' | 'audit';

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  
  const isAdmin = profile?.role === 'system_admin';
  const isManager = profile?.role === 'system_admin' || profile?.role === 'org_manager';
  
  const initialTab = isAdmin ? 'users' : 'lookups';
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);
  
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [resettingPasswordUser, setResettingPasswordUser] = useState<AppUser | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [parentFilter, setParentFilter] = useState('');
  const [sortVector, setSortVector] = useState<'name_asc' | 'date_desc'>('date_desc');

  const { data: settings } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: async () => {
      const { data } = await supabase.from('app_settings').select('*').single();
      return data as AppSettings;
    }
  });

  const { data: allUsers } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: async () => (await supabase.from('users').select('*').order('created_at', { ascending: false })).data as AppUser[],
    enabled: isAdmin && activeTab === 'users'
  });

  const toggleUserStatusMutation = useMutation({
    mutationFn: async ({ userId, status }: { userId: string, status: boolean }) => {
      if (!isAdmin) throw new Error("Personnel access restricted to System Admins.");
      const { data, error } = await supabase.from('users').update({ is_active: status }).eq('id', userId).select();
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("Could not update user status.");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adminUsers'] }),
    onError: (err: any) => alert(`Security Error: ${err.message}`)
  });

  const updateGoogleAuth = useMutation({
    mutationFn: async (val: boolean) => {
      if (!isManager) throw new Error("Governance access restricted.");
      const { data, error } = await supabase.from('app_settings').update({ force_google_auth: val }).eq('id', settings?.id || 1).select();
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adminSettings'] }),
    onError: (err: any) => alert(`Governance Error: ${err.message}`)
  });

  const handleExport = (type: AdminTab) => {
    // Export logic for other tabs if needed
  };

  const tabs = useMemo(() => {
    const list = [];
    if (isAdmin) {
      list.push({ id: 'users', label: 'USER DIRECTORY', icon: Fingerprint });
      list.push({ id: 'audit', label: 'DATA AUDIT', icon: ShieldAlert });
    }
    list.push({ id: 'lookups', label: 'LOOKUP MANAGEMENT', icon: ListTree });
    list.push({ id: 'branding', label: 'BRANDING & STYLES', icon: Palette });
    return list;
  }, [isAdmin]);

  return (
    <div className="space-y-10 pb-24 animate-in fade-in duration-500 max-w-7xl mx-auto">
      <div>
        <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tight">
          {isAdmin ? 'System Administration' : 'Management Settings'}
        </h1>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2 ml-1">
          {isAdmin ? 'Global Access & Governance' : 'Institutional Data Controls'}
        </p>
      </div>

      <div className="flex bg-slate-100/50 p-1.5 rounded-2xl border border-slate-200 overflow-x-auto no-scrollbar gap-1.5 shadow-inner">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id as AdminTab); setSearchTerm(''); setStatusFilter(''); setParentFilter(''); }}
            className={`flex items-center gap-3 px-8 py-4 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap
              ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-lg border border-slate-200 scale-[1.02]' : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        {activeTab === 'users' && isAdmin && (
          <div className="space-y-8">
            <div className="bg-slate-900 rounded-[2rem] p-10 text-white flex flex-col lg:flex-row items-center justify-between gap-8 shadow-2xl relative overflow-hidden">
               <div className="flex items-center gap-8 relative z-10">
                  <div className="w-20 h-20 bg-[#FFDB00] rounded-3xl flex items-center justify-center text-black shadow-2xl">
                    <Shield size={40} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black uppercase tracking-tight leading-none mb-2 text-white">Personnel Governance</h3>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-widest">Global Identity Access</p>
                  </div>
               </div>
               <div className="flex items-center gap-8 bg-white/5 p-6 rounded-2xl border border-white/10 relative z-10">
                  <div className="space-y-1 text-right">
                    <p className="text-[10px] font-black uppercase text-slate-300 tracking-[0.2em]">Enforce Google SSO</p>
                  </div>
                  <div 
                    onClick={() => updateGoogleAuth.mutate(!settings?.force_google_auth)}
                    className={`w-14 h-8 rounded-full flex items-center px-1 transition-all cursor-pointer ${settings?.force_google_auth ? 'bg-[#FFDB00]' : 'bg-slate-700'}`}
                  >
                    <div className={`w-6 h-6 rounded-full bg-white shadow-md transition-all ${settings?.force_google_auth ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
               </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
               <div className="p-8 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight">System Users</h2>
                  <Button onClick={() => setIsAddUserModalOpen(true)} className="px-10 py-4 shadow-xl" icon={<Plus size={18} />}>Add User</Button>
               </div>
               <div className="overflow-x-auto">
                 <table className="w-full text-left">
                    <thead className="bg-slate-50/50 text-slate-400 text-[9px] font-black uppercase tracking-[0.2em] border-b border-slate-100">
                      <tr><th className="px-10 py-5">Name</th><th className="px-10 py-5">Role</th><th className="px-10 py-5 text-center">Status</th><th className="px-10 py-5 text-right">Ops</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {allUsers?.map(user => (
                        <tr key={user.id} className={`group hover:bg-slate-50/80 transition-all ${!user.is_active ? 'opacity-50' : ''}`}>
                          <td className="px-10 py-6">
                            <span className="font-black text-slate-900 text-[13px] uppercase block mb-1">{user.full_name || '—'}</span>
                            <span className="text-slate-400 font-bold text-[10px] lowercase">{user.email}</span>
                          </td>
                          <td className="px-10 py-6">
                             <span className="px-3 py-1 bg-slate-900 text-[#FFDB00] text-[9px] font-black uppercase rounded tracking-widest border border-slate-700">
                                {user.role.replace('_', ' ')}
                             </span>
                          </td>
                          <td className="px-10 py-6 text-center">
                             <div className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border ${user.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                                {user.is_active ? 'ENABLED' : 'DISABLED'}
                             </div>
                          </td>
                          <td className="px-10 py-6 text-right">
                             <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all">
                                <button onClick={() => setResettingPasswordUser(user)} className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-blue-600"><Key size={14} /></button>
                                <button onClick={() => setEditingUser(user)} className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-900"><Edit2 size={14} /></button>
                                <button onClick={() => toggleUserStatusMutation.mutate({ userId: user.id, status: !user.is_active })} className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-rose-600">
                                   {user.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                             </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                 </table>
               </div>
            </div>
          </div>
        )}

        {activeTab === 'audit' && isAdmin && (
           <DataAuditTool />
        )}

        {activeTab === 'lookups' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
            <ReferenceManager />
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="max-w-5xl">
            <BrandingForm settings={settings} />
          </div>
        )}
      </div>

      {resettingPasswordUser && <PasswordModal user={resettingPasswordUser} onClose={() => setResettingPasswordUser(null)} />}
      {(isAddUserModalOpen || editingUser) && (
        <UserFormModal user={editingUser} onClose={() => { setIsAddUserModalOpen(false); setEditingUser(null); }} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['adminUsers'] })} />
      )}
    </div>
  );
};

export default AdminSettings;
